import { useCallback, useEffect, useRef, useState } from 'react';
import { getMessages, markMessageAsRead } from '../../services/messaging';

const MESSAGE_REFRESH_INTERVAL_MS = 5000;
const READ_RETRY_INTERVAL_MS = 30000;
const TERMINAL_ACCESS_CODES = new Set(['permission-denied', 'unauthenticated']);

export const useMessageThreadMessages = ({ conversationId, currentUserId, onConversationUpdate, refreshKey = 0 }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const activeConversationIdRef = useRef(conversationId);
  const requestGenerationRef = useRef(0);
  const readAttemptedAtRef = useRef(new Map());
  activeConversationIdRef.current = conversationId;

  const markUnreadMessagesAsRead = useCallback(async (messagesToCheck, requestConversationId) => {
    if (!currentUserId) return;
    const now = Date.now();
    const unreadMessages = messagesToCheck.filter((message) => {
      if (message.senderId === currentUserId || message.readBy?.[currentUserId]) return false;
      return now - (readAttemptedAtRef.current.get(message.id) || 0) >= READ_RETRY_INTERVAL_MS;
    });
    unreadMessages.forEach((message) => readAttemptedAtRef.current.set(message.id, now));
    const results = await Promise.all(unreadMessages.map((message) => markMessageAsRead(message.id, currentUserId)));
    if (activeConversationIdRef.current !== requestConversationId) return;
    const successCount = results.filter((result) => result?.success).length;
    if (successCount) onConversationUpdate?.();
    if (results.some((result) => !result?.success)) {
      setRefreshError('Messages loaded, but read status could not update. We’ll retry.');
    } else if (results.length) {
      setRefreshError(null);
    }
  }, [currentUserId, onConversationUpdate]);

  const loadMessages = useCallback(async ({ background = false } = {}) => {
    if (!conversationId || !currentUserId) return;
    const requestConversationId = conversationId;
    const requestGeneration = ++requestGenerationRef.current;
    try {
      if (!background) setLoading(true);
      if (!background) setError(null);
      const result = await getMessages(conversationId, currentUserId, { limit: 50, includeDeleted: false });
      if (activeConversationIdRef.current !== requestConversationId || requestGenerationRef.current !== requestGeneration) return;
      if (result.success) {
        const newMessages = result.messages || [];
        setMessages(newMessages);
        setRefreshError((current) => current?.startsWith('Messages loaded, but read status') ? current : null);
        await markUnreadMessagesAsRead(newMessages, requestConversationId);
      } else if (background && !TERMINAL_ACCESS_CODES.has(result.code)) {
        setRefreshError('Messages could not refresh. We’ll keep trying.');
      } else {
        setMessages([]);
        setError(result.error);
      }
    } catch (loadError) {
      if (activeConversationIdRef.current !== requestConversationId || requestGenerationRef.current !== requestGeneration) return;
      if (background && !TERMINAL_ACCESS_CODES.has(loadError?.code)) setRefreshError('Messages could not refresh. We’ll keep trying.');
      else { setMessages([]); setError(loadError.message); }
    } finally {
      if (activeConversationIdRef.current === requestConversationId && requestGenerationRef.current === requestGeneration && !background) setLoading(false);
    }
  }, [conversationId, currentUserId, markUnreadMessagesAsRead]);

  const appendMessage = useCallback((newMessage) => {
    requestGenerationRef.current += 1;
    setMessages((current) => current.some((message) => message.id === newMessage.id) ? current : [...current, newMessage]);
    onConversationUpdate?.();
  }, [onConversationUpdate]);

  useEffect(() => {
    requestGenerationRef.current += 1;
    readAttemptedAtRef.current = new Map();
    setMessages([]);
    setRefreshError(null);
    if (conversationId) loadMessages();
  }, [conversationId, loadMessages, refreshKey]);

  useEffect(() => {
    if (!conversationId || !currentUserId || error) return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled) return;
      if (typeof document === 'undefined' || document.visibilityState === 'visible') await loadMessages({ background: true });
      if (!cancelled) timer = setTimeout(poll, MESSAGE_REFRESH_INTERVAL_MS);
    };
    timer = setTimeout(poll, MESSAGE_REFRESH_INTERVAL_MS);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [conversationId, currentUserId, error, loadMessages]);

  return { messages, loading, error, refreshError, clearRefreshError: () => setRefreshError(null), appendMessage };
};
